import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './MiningMinigame.module.css';
import { ItemData } from '../data/ItemData';
import { gameConfigData } from '../data/data';

const MINING_PHASES = {
  IDLE: 'idle',
  STARTING: 'starting',
  ACTIVE: 'active',
  RESULT: 'result',
};

const MiningMinigame = ({ playerMiningSkill, equippedPickaxeData, onGameEnd }) => {
  const [phase, setPhase] = useState(MINING_PHASES.IDLE);
  const [message, setMessage] = useState('');
  const [timeLeft, setTimeLeft] = useState(8);
  const [clickedOres, setClickedOres] = useState([]);
  const [activeTargets, setActiveTargets] = useState([]); // Multiple targets at once
  const [totalClicks, setTotalClicks] = useState(0);
  
  const gameTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const spawnTimerRef = useRef(null);
  const phaseRef = useRef(MINING_PHASES.IDLE);
  const targetIdCounter = useRef(0);
  
  // Add refs to track current values for game end
  const clickedOresRef = useRef([]);
  const totalClicksRef = useRef(0);

  // Update phase ref whenever phase changes
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  // Update refs when state changes
  useEffect(() => {
    clickedOresRef.current = clickedOres;
  }, [clickedOres]);

  useEffect(() => {
    totalClicksRef.current = totalClicks;
  }, [totalClicks]);

  // Fast-paced game settings
  const gameSettings = React.useMemo(() => ({
    gameDuration: 8000, // 8 seconds total
    spawnInterval: 400, // New targets spawn every 400ms
    targetLifetime: 1200, // Targets live for 1.2 seconds
    maxActiveTargets: 4, // Up to 4 targets visible at once
    minActiveTargets: 2, // Always keep at least 2 targets active
  }), []);

  // Updated ore types with more stone than ore for variety
  const availableOres = React.useMemo(() => [
    { key: 'stone', name: 'Stone', weight: 60, img: '/img/crafting/stone.png' },
    { key: 'copperOre', name: 'Copper Ore', weight: 25, img: '/img/crafting/copperOre.png' },
    { key: 'ironOre', name: 'Iron Ore', weight: 10, img: '/img/crafting/ironOre.png' },
    { key: 'goldOre', name: 'Gold Ore', weight: 5, img: '/img/crafting/goldOre.png' }, // Rare!
  ], []);

  const resetGameState = useCallback(() => {
    setPhase(MINING_PHASES.IDLE);
    setMessage('');
    setTimeLeft(8);
    setClickedOres([]);
    setActiveTargets([]);
    setTotalClicks(0);
    targetIdCounter.current = 0;
    
    // Reset refs
    clickedOresRef.current = [];
    totalClicksRef.current = 0;
    
    // Clear all timers
    if (gameTimerRef.current) clearTimeout(gameTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (spawnTimerRef.current) clearInterval(spawnTimerRef.current);
  }, []);

  // Weighted random selection for ore types
  const getWeightedRandomOre = useCallback(() => {
    const totalWeight = availableOres.reduce((sum, ore) => sum + ore.weight, 0);
    const randomValue = Math.random() * totalWeight;
    
    let cumulativeWeight = 0;
    for (const ore of availableOres) {
      cumulativeWeight += ore.weight;
      if (randomValue <= cumulativeWeight) {
        return ore;
      }
    }
    
    return availableOres[0];
  }, [availableOres]);

  const generateRandomTarget = useCallback(() => {
    const gridSize = 6; // 6x6 grid for more positions
    const positions = [];
    
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        positions.push({
          x: (col * 16.67) + 8.33, // Percentage positions for 6x6 grid
          y: (row * 16.67) + 8.33,
          gridId: `${row}-${col}`
        });
      }
    }
    
    // Find positions not currently occupied
    const occupiedPositions = activeTargets.map(target => target.gridId);
    const availablePositions = positions.filter(pos => !occupiedPositions.includes(pos.gridId));
    
    if (availablePositions.length === 0) return null;
    
    const randomPosition = availablePositions[Math.floor(Math.random() * availablePositions.length)];
    const randomOre = getWeightedRandomOre();
    
    return {
      id: targetIdCounter.current++,
      ...randomPosition,
      ore: randomOre,
      spawnTime: Date.now(),
    };
  }, [activeTargets, getWeightedRandomOre]);

  const spawnTargets = useCallback(() => {
    if (phaseRef.current !== MINING_PHASES.ACTIVE) return;
    
    setActiveTargets(prev => {
      // Remove expired targets
      const now = Date.now();
      const alive = prev.filter(target => 
        now - target.spawnTime < gameSettings.targetLifetime
      );
      
      // Add new targets if we have room and don't have enough
      const newTargets = [...alive];
      const targetsToSpawn = Math.min(
        gameSettings.maxActiveTargets - newTargets.length,
        Math.random() < 0.7 ? 2 : 1 // 70% chance to spawn 2, 30% chance to spawn 1
      );
      
      for (let i = 0; i < targetsToSpawn; i++) {
        const newTarget = generateRandomTarget();
        if (newTarget) {
          newTargets.push(newTarget);
        }
      }
      
      return newTargets;
    });
  }, [generateRandomTarget, gameSettings.targetLifetime, gameSettings.maxActiveTargets]);

  const handleTargetClick = useCallback((targetId, event) => {
    event.stopPropagation();
    
    setActiveTargets(prev => {
      const targetIndex = prev.findIndex(target => target.id === targetId);
      if (targetIndex === -1) return prev;
      
      const clickedTarget = prev[targetIndex];
      
      // Add to clicked ores and update refs immediately
      setClickedOres(prevOres => {
        const newOres = [...prevOres, clickedTarget.ore];
        clickedOresRef.current = newOres; // Keep ref in sync
        return newOres;
      });
      
      setTotalClicks(prevTotal => {
        const newTotal = prevTotal + 1;
        totalClicksRef.current = newTotal; // Keep ref in sync
        return newTotal;
      });
      
      // Remove the clicked target
      return prev.filter(target => target.id !== targetId);
    });
  }, []);

  const endGame = useCallback(() => {
    // Clear all timers first
    if (spawnTimerRef.current) {
      clearInterval(spawnTimerRef.current);
      spawnTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (gameTimerRef.current) {
      clearTimeout(gameTimerRef.current);
      gameTimerRef.current = null;
    }
    
    setPhase(MINING_PHASES.RESULT);
    setMessage('Time\'s up! Great mining session!');
    
    // Wait a moment then end with results using current ref values
    setTimeout(() => {
      onGameEnd({ 
        outcome: 'success', 
        clickedOres: clickedOresRef.current, // Use ref instead of stale closure
        totalClicks: totalClicksRef.current, // Use ref instead of stale closure
        timeSpent: 8
      });
    }, 1500);
  }, [onGameEnd]);

  const startMining = useCallback(() => {
    console.log('[MiningMinigame] Starting fast-paced mining game');
    resetGameState();
    setPhase(MINING_PHASES.STARTING);
    setMessage('Get ready for mining madness! Click as many ore nodes as you can in 8 seconds!');
    
    setTimeout(() => {
      setPhase(MINING_PHASES.ACTIVE);
      setMessage('GO! Click everything you can!');
      
      // Start countdown timer
      setTimeLeft(8);
      countdownRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Start target spawning
      spawnTimerRef.current = setInterval(spawnTargets, gameSettings.spawnInterval);
      
      // End game after 8 seconds
      gameTimerRef.current = setTimeout(() => {
        endGame(); // Use the new endGame function
      }, gameSettings.gameDuration);
      
      // Spawn initial targets
      spawnTargets();
    }, 2000);
  }, [resetGameState, gameSettings, spawnTargets, endGame]);

  useEffect(() => {
    return () => {
      if (gameTimerRef.current) clearTimeout(gameTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (spawnTimerRef.current) clearInterval(spawnTimerRef.current);
    };
  }, []);

  const renderGameArea = () => {
    return (
      <div className={styles.miningArea}>
        {activeTargets.map((target) => (
          <div
            key={target.id}
            className={`${styles.targetSpot} ${styles.activeTarget}`}
            style={{
              left: `${target.x}%`,
              top: `${target.y}%`,
              transform: 'translate(-50%, -50%)',
            }}
            onClick={(event) => handleTargetClick(target.id, event)}
          >
            <img
              src={target.ore.img}
              alt={target.ore.name}
              className={styles.targetIcon}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                pointerEvents: 'none',
                zIndex: 10
              }}
            />
          </div>
        ))}
      </div>
    );
  };

  // Count ores by type for display
  const oreCount = clickedOres.reduce((acc, ore) => {
    acc[ore.key] = (acc[ore.key] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className={styles.miningOverlay}>
      <div className={styles.miningWindow}>
        <h2 className={styles.title}>Mining Frenzy!</h2>
        
        <div className={styles.messageArea}>
          {message}
        </div>
        
        {phase === MINING_PHASES.ACTIVE && (
          <div className={styles.gameStats}>
            <div className={styles.timer}>
              <span className={styles.timerLabel}>Time:</span>
              <span className={styles.timerValue}>{timeLeft}s</span>
            </div>
            <div className={styles.clickCounter}>
              <span className={styles.clickLabel}>Clicks:</span>
              <span className={styles.clickValue}>{totalClicks}</span>
            </div>
          </div>
        )}
        
        {phase === MINING_PHASES.RESULT && (
          <div className={styles.resultsArea}>
            <h3>Mining Results:</h3>
            <div className={styles.oreResults}>
              {Object.entries(oreCount).map(([oreKey, count]) => {
                const ore = availableOres.find(o => o.key === oreKey);
                return (
                  <div key={oreKey} className={styles.oreResult}>
                    <img src={ore.img} alt={ore.name} className={styles.oreResultIcon} />
                    <span>{ore.name}: {count}</span>
                  </div>
                );
              })}
              {Object.keys(oreCount).length === 0 && (
                <p>No ores collected this time. Better luck next time!</p>
              )}
            </div>
            <p className={styles.totalSummary}>
              Total clicks: {totalClicks} | Total ores: {clickedOres.length}
            </p>
          </div>
        )}
        
        {phase === MINING_PHASES.ACTIVE && renderGameArea()}
        
        <div className={styles.actions}>
          {phase === MINING_PHASES.IDLE && (
            <button onClick={startMining} className={styles.actionButton}>
              Start Mining Frenzy
            </button>
          )}
          <button
            onClick={() => onGameEnd({ outcome: 'cancelled' })}
            className={`${styles.actionButton} ${styles.cancelButton}`}
          >
            {phase === MINING_PHASES.RESULT ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MiningMinigame; 